import { gql } from "@apollo/client";
import { BaseMutationTuple, jssaQuery, useBaseMutation } from "./index";
import { ShopifyProduct, ShopifyProductVariant } from "../../../common/models";
import { shopifyProductAllFieldsFragment } from "./CommonQueries";
import { ShopifyProductsInput } from "../../../common/inputs/ShopifyProductsInput";
import { FilterExpressionType } from "../../../common/query-filters";

export const shopifyProductsForAdminQuery = gql`
    ${shopifyProductAllFieldsFragment}
    query shopifyProductsForAdmin($options: ShopifyProductsInput!) {
        shopifyProductsForAdmin(options: $options) {
            ...ShopifyProductAllFields
        }
    }
`;

// Lightweight typeahead query. Reuses the existing `shopifyProductsForAdmin`
// resolver (title `contains` filter) but requests only id + title + store name,
// so the other heavy field resolvers (variants, payout-rule assignments, etc.)
// never run for each keystroke. The store name is shown alongside the title in
// the selector so two similarly-titled products can be told apart.
export const shopifyProductsByTitleQuery = gql`
    query shopifyProductsByTitle($options: ShopifyProductsInput!) {
        shopifyProductsForAdmin(options: $options) {
            id
            title
            store {
                name
            }
            productListing {
                id
            }
        }
    }
`;

export const queryShopifyProductsByTitle = async (
    titleContains: string,
    limit = 20,
    storeId?: string,
): Promise<Array<{ id: string; title: string; storeName: string | null; productListingId: string | null }>> => {
    const expressions: NonNullable<ShopifyProductsInput["expressions"]> = [
        {
            columnName: "title",
            columnValue: [titleContains],
            type: FilterExpressionType.contains,
            caseInsensitive: true,
        },
    ];
    if (storeId) {
        // The resolver maps a `storeName`-column filter carrying the store *id*
        // to `productListing.storeId in [...]`, so this scopes results to the store.
        expressions.push({
            columnName: "storeName",
            columnValue: [storeId],
            type: FilterExpressionType.equals,
        });
    }
    const options: ShopifyProductsInput = {
        pageSize: limit,
        expressions,
    };
    const { data: { shopifyProductsForAdmin } } = await jssaQuery<{
        shopifyProductsForAdmin: Array<{
            id: string;
            title: string | null;
            store: { name: string | null } | null;
            productListing: { id: string | null } | null;
        }>;
    }>({
        query: shopifyProductsByTitleQuery,
        variables: { options },
    });
    return shopifyProductsForAdmin.map(product => ({
        id: String(product.id),
        title: product.title ?? "",
        storeName: product.store?.name ?? null,
        productListingId: product.productListing?.id ?? null,
    }));
};

export const shopifyProductByProductListingIdQuery = gql`
    ${shopifyProductAllFieldsFragment}
    query shopifyProductByProductListingId($productListingId: String!) {
        shopifyProductByProductListingId(productListingId: $productListingId) {
            ...ShopifyProductAllFields
        }
    }
`;

export const connectShopifyProductToStoreMutation = gql`
    ${shopifyProductAllFieldsFragment}
    mutation connectShopifyProductToStore($shopifyProductId: BigInt!, $storeId: String!) {
        connectShopifyProductToStore(shopifyProductId: $shopifyProductId, storeId: $storeId) {
            ...ShopifyProductAllFields
        }
    }
`;

export const queryShopifyProductsForAdmin = async (
    options: ShopifyProductsInput,
): Promise<ShopifyProduct[]> => {
    const { data: { shopifyProductsForAdmin } } = await jssaQuery<{ shopifyProductsForAdmin: ShopifyProduct[] }>({
        query: shopifyProductsForAdminQuery,
        variables: {
            options,
        },
    });
    return shopifyProductsForAdmin.map(product => ({
        ...product,
        id: String(product.id),
    }));
};

export const queryShopifyProductByProductListingId = async (
    productListingId: string,
): Promise<ShopifyProduct | null> => {
    const { data: { shopifyProductByProductListingId } } = await jssaQuery<{ shopifyProductByProductListingId: ShopifyProduct | null }>({
        query: shopifyProductByProductListingIdQuery,
        variables: {
            productListingId,
        },
    });
    return shopifyProductByProductListingId;
};

export const variantsWithCostMetadataQuery = gql`
    query variantsWithCostMetadata($productListingId: String!) {
        shopifyProductByProductListingId(productListingId: $productListingId) {
            variants {
                id
                sku
                title
                price
                compareAtPrice
                inventoryQuantity
                costMetadata {
                    id
                }
            }
        }
    }
`;

export type VariantWithCostMetadataCount = ShopifyProductVariant & { costMetadata?: { id: string }[] };

export const queryVariantsWithCostMetadata = async (
    productListingId: string,
): Promise<VariantWithCostMetadataCount[]> => {
    const { data: { shopifyProductByProductListingId } } = await jssaQuery<{
        shopifyProductByProductListingId: { variants?: VariantWithCostMetadataCount[] } | null
    }>({
        query: variantsWithCostMetadataQuery,
        variables: { productListingId },
        fetchPolicy: 'no-cache',
    });
    if (!shopifyProductByProductListingId?.variants) {
        return [];
    }
    return shopifyProductByProductListingId.variants.map(v => ({
        ...v,
        id: String(v.id),
    }));
};

export const variantsWithoutCostMetadataQuery = gql`
    query variantsWithoutCostMetadata($shopifyProductId: String!) {
        variantsWithoutCostMetadata(shopifyProductId: $shopifyProductId) {
            id
            sku
            title
        }
    }
`;

export const queryVariantsWithoutCostMetadata = async (
    shopifyProductId: string,
): Promise<ShopifyProductVariant[]> => {
    const { data: { variantsWithoutCostMetadata } } = await jssaQuery<{ variantsWithoutCostMetadata: ShopifyProductVariant[] }>({
        query: variantsWithoutCostMetadataQuery,
        variables: { shopifyProductId },
    });
    return variantsWithoutCostMetadata.map(variant => ({
        ...variant,
        id: String(variant.id),
    }));
};

export const queryAllVariantsByProductListingId = async (
    productListingId: string,
): Promise<ShopifyProductVariant[]> => {
    const product = await queryShopifyProductByProductListingId(productListingId) as (ShopifyProduct & { variants?: ShopifyProductVariant[] }) | null;
    if (!product?.variants) {
        return [];
    }
    return product.variants.map((variant: ShopifyProductVariant) => ({
        ...variant,
        id: String(variant.id),
    }));
};

export const useConnectShopifyProductToStoreMutation = (): BaseMutationTuple<{ shopifyProductId: string; storeId: string }, ShopifyProduct> => {
    const transformInput = (input: { shopifyProductId: string; storeId: string }) => {
        return {
            shopifyProductId: input.shopifyProductId,
            storeId: input.storeId,
        };
    };

    const transformOutput = (output: any) => {
        return output.connectShopifyProductToStore;
    };

    return useBaseMutation<{ shopifyProductId: string; storeId: string }, ShopifyProduct>(
        connectShopifyProductToStoreMutation,
        transformInput,
        transformOutput,
    );
};
